// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Badge, Group, Paper, Table, Text, Title } from '@mantine/core';
import type { ChargeItem } from '@medplum/fhirtypes';
import { Loading, useMedplum } from '@medplum/react';
import { formatUsd } from '@polarissleep/shared';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';

interface ChargesSectionProps {
  patientId: string;
}

function statusBadge(status: ChargeItem['status']): JSX.Element {
  if (status === 'billed') {
    return <Badge color="green">Paid</Badge>;
  }
  if (status === 'billable' || status === 'planned') {
    return <Badge color="yellow">Pending</Badge>;
  }
  return <Badge color="gray">{status}</Badge>;
}

function getDescription(item: ChargeItem): string {
  return item.code?.coding?.[0]?.display ?? item.code?.text ?? '—';
}

export function ChargesSection({ patientId }: ChargesSectionProps): JSX.Element {
  const medplum = useMedplum();
  const [chargeItems, setChargeItems] = useState<ChargeItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    medplum
      .searchResources('ChargeItem', { subject: `Patient/${patientId}`, _sort: '-occurrence' })
      .then(setChargeItems)
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [medplum, patientId]);

  return (
    <Paper withBorder p="lg" radius="md">
      <Group justify="space-between" mb="md">
        <Title order={3}>History of Charges</Title>
      </Group>
      {loading && <Loading />}
      {!loading && chargeItems.length === 0 && <Text c="dimmed">No payment history.</Text>}
      {!loading && chargeItems.length > 0 && (
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Purpose</Table.Th>
              <Table.Th>Insurance Contribution</Table.Th>
              <Table.Th>Est. Patient Contribution</Table.Th>
              <Table.Th>Actual Patient Contribution</Table.Th>
              <Table.Th>Balance Due</Table.Th>
              <Table.Th>Status</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {chargeItems.map((item) => (
              <Table.Tr key={item.id}>
                <Table.Td>{getDescription(item)}</Table.Td>
                <Table.Td>---</Table.Td>
                <Table.Td>---</Table.Td>
                <Table.Td>{formatUsd(item.priceOverride?.value ?? 0)}</Table.Td>
                <Table.Td>{item.status === 'billed' ? 'N/A' : formatUsd(item.priceOverride?.value ?? 0)}</Table.Td>
                <Table.Td>{statusBadge(item.status)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Paper>
  );
}
